if (!window.CusaPetitionPostSignatureFunnel) {
	/**
	 * Manages the multi-step post-petition-signature funnel: card accordion animation,
	 * step-map navigation, in-funnel sponsorship add-to-cart, and funnel analytics.
	 * Initialised once on DOMContentLoaded against #petition-post-signature-funnel.
	 */
	class CusaPetitionPostSignatureFunnel {
		/**
		 * @param {HTMLElement} root - The #petition-post-signature-funnel section element
		 */
		constructor(root) {
			this.root = root;
			this.cards = Array.from(root.querySelectorAll('.petition-funnel-card'));
			this.stepItems = Array.from(root.querySelectorAll('.petition-funnel-step-map [data-step]'));
			this.activeIndex = 0;
			this.isSubmitting = false;

			if (!this.cards.length) return;

			this.bindStepMap();
			this.bindSkipBackButtons();
			this.bindSponsorshipButtons();
			this.observeStepMapHeight();

			// Track the initial view of step 2 — openCard is never called on load since
			// the first card is pre-expanded in HTML. We use IntersectionObserver so the
			// event only fires when the funnel actually becomes visible (i.e. after the
			// user signs), not on page load while it's still hidden in the reveal container.
			this.observeInitialView();
		}

		/**
		 * Wires up step-map buttons so clicking a step navigates directly to that card.
		 * @returns {void}
		 */
		bindStepMap() {
			this.stepItems.forEach((item, index) => {
				item.querySelector('button')?.addEventListener('click', () => this.openCard(index));
			});
		}

		/**
		 * Delegates skip (forward) and back navigation from the card action buttons.
		 * @returns {void}
		 */
		bindSkipBackButtons() {
			this.root.addEventListener('click', (event) => {
				if (event.target.closest('[data-cusa-funnel-skip]')) {
					this.openCard(this.activeIndex + 1);
				} else if (event.target.closest('[data-cusa-funnel-back]')) {
					this.openCard(this.activeIndex - 1);
				}
			});
		}

		/**
		 * Attaches the sponsorship click handler to all sponsorship buttons in the funnel.
		 * @returns {void}
		 */
		bindSponsorshipButtons() {
			this.root.querySelectorAll('[data-cusa-petition-sponsorship-button]').forEach((btn) => {
				btn.addEventListener('click', this.handleSponsorshipClick);
			});
		}

		/**
		 * Click handler for in-funnel sponsorship buttons — adds the variant to the cart,
		 * opens the cart drawer, and advances to the next funnel step once the drawer closes.
		 * @param {MouseEvent} event
		 * @returns {Promise<void>}
		 */
		handleSponsorshipClick = async (event) => {
			if (this.isSubmitting) return;

			const button = event.currentTarget;
			const variantId = button.dataset.variantId;
			const petitionHandle = button.dataset.petitionHandle;

			if (!variantId || !petitionHandle) {
				this.showErrorToast();
				return;
			}

			// Fire on the click itself, not on add-to-cart success — this measures tier
			// selection, and a failed cart call is still a selection the user made.
			window.CusaPetitionFunnelAnalytics?.trackContribution(button.dataset.dollarAmount);

			const card = button.closest('.petition-funnel-card');
			const cardIndex = parseInt(card?.dataset.cardIndex ?? '0', 10);
			const btnGrid = button.closest('.petition-funnel-sponsorship-btns');
			const buttons = btnGrid
				? Array.from(btnGrid.querySelectorAll('[data-cusa-petition-sponsorship-button]'))
				: [button];

			this.isSubmitting = true;
			if (btnGrid) btnGrid.setAttribute('aria-busy', 'true');

			buttons.forEach((b) => {
				b.disabled = true;
				b.setAttribute('aria-disabled', 'true');
				if (b === button) {
					b.classList.add('loading');
					b.querySelector('[data-cusa-petition-sponsorship-text]')?.classList.add('hidden');
					b.querySelector('.loading__spinner')?.classList.remove('hidden');
				}
			});

			try {
				const cartData = await this.addToCart({ variantId, petitionHandle });
				await this.handleSponsorshipSuccess(cartData, cardIndex, buttons);
			} catch (error) {
				console.warn('[CUSA Petition Funnel] add to cart failed', error);
				buttons.forEach((b) => {
					b.disabled = false;
					b.removeAttribute('aria-disabled');
					b.classList.remove('loading');
					b.querySelector('[data-cusa-petition-sponsorship-text]')?.classList.remove('hidden');
					b.querySelector('.loading__spinner')?.classList.add('hidden');
				});
				this.showErrorToast();
			} finally {
				if (btnGrid) btnGrid.removeAttribute('aria-busy');
				this.isSubmitting = false;
			}
		};

		/**
		 * Adds the sponsorship variant to the Shopify cart via the Cart API,
		 * including section-rendering data so the cart drawer can update without a reload.
		 * @param {{ variantId: string, petitionHandle: string }} params
		 * @returns {Promise<Object>} Parsed cart response data
		 * @throws {Object} Cart API error response if the request fails
		 */
		addToCart = async ({ variantId, petitionHandle }) => {
			const cartDrawer = document.querySelector('cart-drawer');
			const cartNotification = document.querySelector('cart-notification');
			const cartComponent = cartDrawer || cartNotification;
			const formData = new FormData();
			formData.append('id', variantId);
			formData.append('quantity', '1');
			formData.append('properties[_sponsored-petition-handle]', petitionHandle);

			if (cartComponent && typeof cartComponent.getSectionsToRender === 'function') {
				formData.append(
					'sections',
					cartComponent.getSectionsToRender().map((section) => section.id)
				);
				formData.append('sections_url', window.location.pathname);
				if (typeof cartComponent.setActiveElement === 'function') {
					cartComponent.setActiveElement(document.activeElement);
				}
			}

			const config =
				typeof window.fetchConfig === 'function'
					? window.fetchConfig('javascript')
					: {
							method: 'POST',
							headers: { 'X-Requested-With': 'XMLHttpRequest' }
						};

			if (!config.headers) config.headers = {};
			config.headers['X-Requested-With'] = 'XMLHttpRequest';
			delete config.headers['Content-Type'];
			config.body = formData;

			const addBaseUrl = window?.routes?.cart_add_url || '/cart/add';
			const addUrl = addBaseUrl.endsWith('.js') ? addBaseUrl : `${addBaseUrl}.js`;
			const response = await fetch(addUrl, config);
			const data = await response.json();

			if (!response.ok || data?.status) throw data;
			return data;
		};

		/**
		 * Handles a successful in-funnel sponsorship: renders the cart drawer, opens it,
		 * and advances the funnel after the customer closes the drawer.
		 * @param {Object} cartData - Parsed response from the Shopify Cart API
		 * @param {number} cardIndex - Zero-based index of the card that triggered the sponsorship
		 * @param {HTMLButtonElement[]} buttons - All buttons in the sponsorship grid
		 * @returns {Promise<void>}
		 */
		handleSponsorshipSuccess = async (cartData, cardIndex, buttons) => {
			const cartDrawer = document.querySelector('cart-drawer');
			const cartNotification = document.querySelector('cart-notification');
			const cartComponent = cartDrawer || cartNotification;
			const usingCartDrawer = cartComponent === cartDrawer;

			await this.syncRevealContainerHeight();

			if (cartComponent?.classList?.contains('is-empty')) {
				cartComponent.classList.remove('is-empty');
			}
			const drawerInner = cartDrawer?.querySelector('.drawer__inner');
			if (drawerInner?.classList.contains('is-empty')) {
				drawerInner.classList.remove('is-empty');
			}

			if (usingCartDrawer && cartDrawer) {
				this.renderDrawerContentsWithoutOpening(cartDrawer, cartData);
			} else if (cartComponent && typeof cartComponent.renderContents === 'function') {
				cartComponent.renderContents(cartData);
			}

			if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
				window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
					source: 'cusa-petition-post-signature-funnel',
					cartData
				});
			}

			await new Promise((resolve) => setTimeout(resolve, 1000));

			buttons.forEach((b) => {
				b.disabled = false;
				b.removeAttribute('aria-disabled');
				b.classList.remove('loading');
				b.querySelector('[data-cusa-petition-sponsorship-text]')?.classList.remove('hidden');
				b.querySelector('.loading__spinner')?.classList.add('hidden');
			});

			const nextIndex = cardIndex + 1;

			if (usingCartDrawer && cartDrawer && typeof cartDrawer.open === 'function') {
				const cartTrigger = document.querySelector('#cart-icon-bubble') || undefined;
				// renderDrawerContentsWithoutOpening replaces the drawer's innerHTML, destroying the
				// overlay click listener bound in the constructor. Re-attach it before opening.
				const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
				if (overlay) overlay.addEventListener('click', cartDrawer.close.bind(cartDrawer));
				cartDrawer.open(cartTrigger);
				await this.syncRevealContainerHeight();

				// Advance to the next step only after the customer closes the cart drawer.
				// Double rAF lets the browser reflow after overflow-hidden is removed from
				// body (scrollbar reappears) before we read scroll/layout metrics in openCard.
				if (nextIndex < this.cards.length) {
					await this.waitForDrawerClose(cartDrawer);
					await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
					this.openCard(nextIndex);
				}
			} else {
				if (nextIndex < this.cards.length) {
					this.openCard(nextIndex);
				}
			}
		};

		/**
		 * Updates the cart drawer's section HTML without triggering its open animation.
		 * Needed because calling cartDrawer.open() before section HTML is ready causes a flicker.
		 * @param {HTMLElement} cartDrawer - The cart-drawer custom element
		 * @param {Object} parsedState - Parsed cart response containing section HTML
		 * @returns {void}
		 */
		renderDrawerContentsWithoutOpening = (cartDrawer, parsedState) => {
			if (!cartDrawer || !parsedState) return;
			const drawerInner = cartDrawer.querySelector('.drawer__inner');
			if (drawerInner?.classList.contains('is-empty')) {
				drawerInner.classList.remove('is-empty');
			}
			cartDrawer.productId = parsedState.id;
			if (
				typeof cartDrawer.getSectionsToRender === 'function' &&
				typeof cartDrawer.getSectionInnerHTML === 'function'
			) {
				cartDrawer.getSectionsToRender().forEach((section) => {
					const sectionElement = section.selector
						? document.querySelector(section.selector)
						: document.getElementById(section.id);
					if (!sectionElement || !parsedState.sections?.[section.id]) return;
					sectionElement.innerHTML = cartDrawer.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
				});
			}
		};

		/**
		 * Expands the card at the given index, collapses all others, updates the step map,
		 * fires a funnel-view analytics event, and scrolls the funnel into view.
		 * @param {number} index - Zero-based index of the card to open
		 * @returns {void}
		 */
		openCard(index) {
			if (index < 0 || index >= this.cards.length) return;

			this.cards.forEach((_, i) => {
				if (i !== index) this.collapseCard(i);
			});

			const card = this.cards[index];
			const body = card.querySelector('.petition-funnel-card-body');

			card.setAttribute('aria-expanded', 'true');

			const bodyInner = card.querySelector('.petition-funnel-card-body-inner');
			if (bodyInner) bodyInner.scrollTop = 0;

			if (body) {
				const targetHeight = body.scrollHeight;
				body.style.height = targetHeight + 'px';
				body.addEventListener(
					'transitionend',
					() => {
						if (card.getAttribute('aria-expanded') === 'true') {
							body.style.height = 'auto';
							this.syncRevealContainerHeight();
							requestAnimationFrame(() => this.scrollToFunnel());
						}
					},
					{ once: true }
				);
			}

			const direction = index < this.activeIndex ? 'back' : 'forward';
			this.activeIndex = index;
			this.setActiveStep(index);
			this.syncRevealContainerHeight();
			if (typeof initializeShareButtons === 'function') initializeShareButtons(card);
			this.trackFunnelView(index, direction);
			this.trackStepView(index, direction);
		}

		/**
		 * Fires the rebuilt `petition_step_view` for a forward step transition.
		 *
		 * Backward navigation and step-map jumps to an earlier card are deliberately
		 * silent — re-opening a card is not a step entry, and counting it is what made
		 * the previous schema behave like a page counter. The support step is not fired
		 * here at all: reaching support is a signature (or a direct member arrival), and
		 * that transition is reported from signedStateUpdate where the entry reason is
		 * actually known.
		 *
		 * The step name comes from the step-map label rather than a hardcoded index map,
		 * since the cards are metaobject-driven and their count can change.
		 *
		 * @param {number} index - Zero-based index of the card being opened
		 * @param {'forward'|'back'} direction
		 * @returns {void}
		 */
		trackStepView(index, direction) {
			if (direction !== 'forward') return;
			if (!window.CusaPetitionFunnelAnalytics) return;

			const stepLabel = this.root.querySelector(
				`.petition-funnel-step-map [data-step="${index}"] .petition-funnel-step-label`
			);
			const step = stepLabel?.textContent?.trim().toLowerCase();
			if (!step || step === 'support') return;

			window.CusaPetitionFunnelAnalytics.trackStepView(step);
		}

		/**
		 * Uses IntersectionObserver to fire the initial petition_funnel_view_2 event only
		 * when the funnel first becomes visible — i.e. after the user signs and the reveal
		 * container opens, not on page load while it is still hidden.
		 * @returns {void}
		 */
		observeInitialView() {
			if (typeof IntersectionObserver === 'undefined') return;
			const observer = new IntersectionObserver(
				(entries) => {
					if (entries[0].isIntersecting) {
						observer.disconnect();
						this.trackFunnelView(0, 'forward');
					}
				},
				{ threshold: 0.1 }
			);
			observer.observe(this.root);
		}

		/**
		 * Fires a petition_funnel_view_N analytics event for the given card index.
		 * Step numbering is 1-based from the user's perspective, where step 1 is the
		 * petition signing itself — so the first funnel card maps to step_number 2.
		 * @param {number} index - Zero-based index of the funnel card being viewed
		 * @param {'forward'|'back'} direction - Whether the user navigated forward or backward
		 * @returns {void}
		 */
		trackFunnelView(index, direction) {
			if ('function' !== typeof sendAnalyticsEvent) return;
			const stepNumber = index + 2;
			const stepLabel = this.root.querySelector(`.petition-funnel-step-map [data-step="${index}"] .petition-funnel-step-label`);
			const stepName = stepLabel?.textContent?.trim() ?? '';
			sendAnalyticsEvent('petition_funnel_view_' + stepNumber, {
				item_id: 'function' === typeof getSlug ? getSlug(window.location.href) : '',
				direction,
				step_number: stepNumber,
				step_name: stepName
			});
		}

		/**
		 * Smooth-scrolls the page so the funnel root sits just below the sticky header.
		 * @returns {void}
		 */
		scrollToFunnel() {
			const stickyHeader = document.querySelector('sticky-header');
			const headerBottom = stickyHeader ? stickyHeader.getBoundingClientRect().bottom : 0;
			// Target the funnel root (not the sticky step map — its getBoundingClientRect
			// returns its pinned position while sticking, which breaks the offset math)
			const funnelTop = this.root.getBoundingClientRect().top + window.scrollY;
			window.scrollTo({ top: funnelTop - headerBottom - 12, behavior: 'smooth' });
		}

		/**
		 * Animates the card at the given index to a collapsed (zero-height) state.
		 * @param {number} index - Zero-based index of the card to collapse
		 * @returns {void}
		 */
		collapseCard(index) {
			const card = this.cards[index];
			if (!card) return;
			const body = card.querySelector('.petition-funnel-card-body');

			card.setAttribute('aria-expanded', 'false');

			if (body && body.style.height !== '0px') {
				body.style.height = body.scrollHeight + 'px';
				body.offsetHeight; // force reflow
				requestAnimationFrame(() => {
					body.style.height = '0px';
				});
			}
		}

		/**
		 * Marks step-map items as completed or current based on the active card index.
		 * @param {number} index - Zero-based index of the currently active card
		 * @returns {void}
		 */
		setActiveStep(index) {
			this.stepItems.forEach((item) => {
				const stepIndex = parseInt(item.dataset.step, 10);
				item.removeAttribute('aria-current');
				if (stepIndex < index) {
					item.setAttribute('data-completed', '');
				} else {
					item.removeAttribute('data-completed');
				}
			});
			if (index !== null) {
				const stepItem = this.root.querySelector(`.petition-funnel-step-map [data-step="${index}"]`);
				if (stepItem) stepItem.setAttribute('aria-current', 'step');
			}
		}

		/**
		 * Keeps --funnel-step-map-height and --funnel-header-height CSS custom properties
		 * in sync with the actual measured heights, enabling the sticky step map to sit
		 * flush below the sticky site header.
		 * @returns {void}
		 */
		observeStepMapHeight() {
			const stepMap = this.root.querySelector('.petition-funnel-step-map');
			if (stepMap) {
				const update = () => this.root.style.setProperty('--funnel-step-map-height', stepMap.offsetHeight + 'px');
				update();
				new ResizeObserver(update).observe(stepMap);
			}

			const updateHeaderHeight = () => {
				// .shopify-section-header-sticky is the same element the theme queries
				// for --sticky-avoiding-nav, but the theme adds an extra 20px offset.
				// We use the raw offsetHeight so the step map sits flush to the header.
				const header = document.querySelector('.shopify-section-header-sticky');
				if (!header) return;
				this.root.style.setProperty('--funnel-header-height', header.offsetHeight + 'px');
			};
			updateHeaderHeight();
			// Re-measure on viewport resize (header height can change responsively).
			// Intentionally NOT using ResizeObserver — sticky headers change offsetHeight
			// during scroll when they collapse, which would mutate `top` mid-scroll and
			// cause the step map to suddenly unstick.
			window.addEventListener('resize', updateHeaderHeight, { passive: true });
		}

		/**
		 * Resolves once the cart drawer's 'active' class is removed (i.e. the drawer is closed).
		 * @param {HTMLElement} cartDrawer - The cart-drawer custom element to watch
		 * @returns {Promise<void>}
		 */
		waitForDrawerClose(cartDrawer) {
			return new Promise((resolve) => {
				if (!cartDrawer.classList.contains('active')) {
					resolve();
					return;
				}
				const observer = new MutationObserver(() => {
					if (!cartDrawer.classList.contains('active')) {
						observer.disconnect();
						resolve();
					}
				});
				observer.observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
			});
		}

		/**
		 * Updates the nearest .reveal ancestor's height to fit its current content.
		 * Must be called after any DOM change that alters the funnel's natural height.
		 * @returns {Promise<void>}
		 */
		syncRevealContainerHeight = () => {
			return new Promise((resolve) => {
				const revealContainer = this.root.closest('.reveal');
				if (!revealContainer) {
					resolve();
					return;
				}
				requestAnimationFrame(() => {
					revealContainer.style.removeProperty('height');
					revealContainer.style.height = 'auto';
					resolve();
				});
			});
		};

		/**
		 * Shows an error toast notification, or logs a fallback warning if the
		 * toast system is unavailable.
		 * @returns {void}
		 */
		showErrorToast = () => {
			if (window.CusaToastNotification?.create) {
				window.CusaToastNotification.create({
					type: 'error',
					title: 'Unable to add sponsorship',
					message: "We couldn't add your sponsorship. Please try again.",
					duration: 'infinite'
				});
				return;
			}
			console.warn("We couldn't add your sponsorship. Please try again.");
		};
	}

	window.CusaPetitionPostSignatureFunnel = CusaPetitionPostSignatureFunnel;

	window.addEventListener('DOMContentLoaded', () => {
		const root = document.getElementById('petition-post-signature-funnel');
		if (root) new CusaPetitionPostSignatureFunnel(root);
	});
}
